import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { client, mapServerErrors } from "shared/helpers/client";
import { useMutation } from "react-query";
import { useAuthContext } from "shared/context/auth-context";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { LanguageAtom, ServerLanguage } from "shared/helpers/language";

interface LoginResponse {
  token: string;
  refreshToken: string;
}

interface LoginData {
  username: string;
  password: string;
  rememberMe: boolean;
}

export function useLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [serverErrors,setServerErrors] = React.useState<object>({});
  const [{ isLoggedIn }, dispatch] = useAuthContext();
  const { mutate: callLoginServer, isLoading } = useMutation((loginDTO: any) =>
    client<LoginResponse>("/Owner/Login", { data: loginDTO })
  );

  const schema = React.useMemo(() => {
    return yup.object().shape({
      username: yup.string().required(t("errors.required")),
      password: yup.string().required(t("errors.required")),
      rememberMe: yup.boolean(),
    });
  }, [t]);

  const methods = useForm<LoginData>({
    resolver: yupResolver(schema),
  });

  const login = async (data: LoginData) => {
    const loginDTO = {
      userName: data.username,
      password: data.password,
    };

    callLoginServer(loginDTO, {
      onSuccess({ response, error }) {
        if (error) return setServerErrors(mapServerErrors(error));
        if (response) {
          dispatch({
            type: "login",
            token: response.token,
            staySignedIn: data.rememberMe,
            refresh: response.refreshToken,
          });
          navigate('/')
        }
      },
    });
  };

  return {
    ...methods,
    onSubmit: methods.handleSubmit(login),
    isLoading,
    isLoggedIn,
    serverErrors
  };
}
